import React from 'react';
import styled from 'styled-components';
import { useSearchParams } from '~/shared/hooks';
import FilterBarBase, { NarrativeSelection } from './FilterBarBase';
import NarrativeChips from './NarrativeChips';

type FilterBarProps = React.ComponentProps<typeof FilterBarBase>;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
`;

const FilterContainer = styled.div<{ $hasChips: boolean }>`
  width: 100%;
  margin-bottom: ${({ $hasChips }) => ($hasChips ? '20px' : '0')};
`;

const ChipsRegion = styled.div<{ $hasChips: boolean }>`
  width: 100%;
  margin-top: ${({ $hasChips }) => ($hasChips ? '-20px' : '0')};
`;

export type { NarrativeSelection } from './FilterBarBase';

export default function FilterBar(props: FilterBarProps) {
  const { onNarrativeSelectionChange, ...rest } = props as FilterBarProps & {
    onNarrativeSelectionChange?: (selections: NarrativeSelection[]) => void;
  };
  const [narrativeSelections, setNarrativeSelections] = React.useState<NarrativeSelection[]>([]);
  const { setParams } = useSearchParams();

  const resolveNarrativeFallbackValue = React.useCallback(() => {
    const allFilters = (rest as any)?.filters;
    if (!Array.isArray(allFilters)) {
      return '';
    }

    const narrativeFilter = allFilters.find((filter: any) => filter?.name === 'Narrative');
    if (!narrativeFilter) {
      return '';
    }

    const resolveCandidate = (candidate: string | number | null | undefined) => {
      if (candidate === undefined || candidate === null) return null;
      const trimmed = typeof candidate === 'string' ? candidate.trim() : candidate;
      if (trimmed === undefined || trimmed === null || trimmed === '') return null;
      return String(trimmed);
    };

    const defaultValue = narrativeFilter?.defaultValue ?? narrativeFilter?.['default-value'];
    const fallbackFromDefault = Array.isArray(defaultValue)
      ? (defaultValue as Array<string | number>)
          .map(resolveCandidate)
          .filter((candidate): candidate is string => candidate !== null)
      : typeof defaultValue === 'string' && defaultValue.trim()
        ? defaultValue
            .split(',')
            .map(token => resolveCandidate(token))
            .filter((candidate): candidate is string => candidate !== null)
        : [];

    if (fallbackFromDefault.length > 0) {
      return fallbackFromDefault.join(',');
    }

    const options = Array.isArray(narrativeFilter?.options) ? narrativeFilter.options : [];
    const firstOption = options[0];
    const resolved = resolveCandidate(
      firstOption?.id ?? firstOption?.value ?? firstOption?.iso ?? firstOption?.name ?? null,
    );
    if (!resolved) {
      return '';
    }

    return resolved;
  }, [rest]);

  const handleNarrativeSelectionChange = React.useCallback(
    (selections: NarrativeSelection[]) => {
      setNarrativeSelections(selections);
      onNarrativeSelectionChange?.(selections);
    },
    [onNarrativeSelectionChange],
  );

  const handleNarrativeChipRemove = React.useCallback(
    (chipId: string) => {
      const remainingSelections = narrativeSelections.filter(selection => selection.id !== chipId);
      const remainingIds = remainingSelections.map(selection => selection.id);
      setNarrativeSelections(remainingSelections);
      onNarrativeSelectionChange?.(remainingSelections);
      if (remainingIds.length === 0) {
        const fallbackValue = resolveNarrativeFallbackValue();
        setParams({ newParams: { Narrative: fallbackValue } });
        return;
      }
      setParams({ newParams: { Narrative: remainingIds.join(',') } });
    },
    [narrativeSelections, onNarrativeSelectionChange, resolveNarrativeFallbackValue, setParams],
  );

  const hasChips = narrativeSelections.length > 1;

  return (
    <Wrapper>
      <FilterContainer $hasChips={hasChips}>
        <FilterBarBase {...rest} onNarrativeSelectionChange={handleNarrativeSelectionChange} />
      </FilterContainer>
      <ChipsRegion $hasChips={hasChips}>
        <NarrativeChips selections={narrativeSelections} onRemove={handleNarrativeChipRemove} />
      </ChipsRegion>
    </Wrapper>
  );
}
